import { createListenerMiddleware } from '@reduxjs/toolkit';
import {
    setProject,
    addFeature,
    addFeatures,
    removeFeature,
    removeFeatures,
    updateFeatureSetting,
    projectSetDebugTarget,
    projectSetUploadMethod,
    getProjectCode,
    swapFeatures,
    setProjectProperty,
    createCloudDevice,
} from '@appblocks/frontend/redux/projectSlice';
import Session from 'supertokens-auth-react/recipe/session';
import { inIframe, axiosInstance, API_CODY_URL } from '@appblocks/frontend/client/utils';
import ProjectInstance from '@appblocks/frontend/client/ProjectInstance';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';

const axios = require('axios');
const pluralize = require('pluralize');

const SAVE_INTERVAL = 500;
let globalStore = null;

const projectMiddleware = createListenerMiddleware();
let updateQueue = [];

let worker;

const pushUpdate = async (store) => {
    globalStore = store;
    const project = ProjectInstance.getInstance();
    if (!project) {
        return;
    }
    globalStore.dispatch({
        type: 'project/setLastValidated',
        payload: undefined,
    });
    const projectJson = project.toJson();
    updateQueue.push(projectJson);
};

const doUpdate = async () => {
    if (!globalStore) {
        return;
    }
    const { projectEditable } = globalStore.getState().pages;
    if (updateQueue.length === 0 || !projectEditable) {
        return;
    }
    const project = updateQueue.pop();
    updateQueue = [];
    globalStore.dispatch({
        type: 'project/projectSetBinaryId',
        payload: {
            projectId: project.id,
            binaryId: undefined,
        },
    });
    project.updatedAt = new Date().toUTCString();
    if (worker === undefined) {
        worker = new Worker(new URL('./validationWorker.js', import.meta.url));
        worker.onmessage = validationComplete;
    }
    worker.postMessage(project);
};

const validationComplete = async (e) => {
    const validationMessages = e.data;
    const errorsList = validationMessages.filter(m => m.messageType === 'error');

    const projectInstance = ProjectInstance.getInstance();
    if (!projectInstance) {
        return;
    }
    const project = projectInstance.toJson();
    const errorsHashmap = {};
    const getErrorInParent = (featureId, settingIds, message) => {
        const feature = projectInstance.getFeature(featureId);
        if (feature === undefined) {
            return;
        }
        if (errorsHashmap[featureId] === undefined) {
            errorsHashmap[featureId] = {};
        }
        if (message !== undefined) {
            const insert = settingIds
                .reduce((acc, settingId) => ({ ...acc, [settingId]: message }), {});
            errorsHashmap[featureId] = { ...errorsHashmap[featureId], ...insert };
        }

        for (let i = 0; i < feature.settings.length; i++) {
            if (feature.settings[i].group !== undefined
                && settingIds.includes(feature.settings[i].id)) {
                errorsHashmap[featureId][feature.settings[i].group] = message;
            }
        }
        const parent = projectInstance.getParent(feature);
        if (parent === undefined) {
            return;
        }
        const parentId = parent.getID();
        const settingInParentIds = [];
        for (let i = 0; i < parent.settings.length; i++) {
            if (Array.isArray(parent.settings[i].value)) {
                for (let j = 0; j < parent.settings[i].value.length; j++) {
                    if (parent.settings[i].value[j].getID() === feature.getID()) {
                        settingInParentIds.push(parent.settings[i].id);
                        if (parent.settings[i].group !== undefined) {
                            settingInParentIds.push(parent.settings[i].group);
                        }
                        break;
                    }
                }
            }
        }
        return getErrorInParent(parentId, settingInParentIds, '');
    };
    for (let i = 0; i < errorsList.length; i++) {
        const { featureId, message, settingId } = errorsList[i];
        getErrorInParent(featureId, [settingId], message);
    }

    const previousErrors = globalStore.getState().projectData.validationErrors;
    const previousErrorsMap = new Map();
    for (let i = 0; i < previousErrors.length; i++) {
        previousErrorsMap.set(JSON.stringify(previousErrors[i]), previousErrors[i].featureId);
    }
    const errorsMap = new Map();
    for (let i = 0; i < errorsList.length; i++) {
        errorsMap.set(JSON.stringify(errorsList[i]), errorsList[i].featureId);
    }

    globalStore.dispatch({
        type: 'project/setValidationErrors',
        payload: errorsList,
    });

    const clearedErrors = previousErrors.filter(e => !errorsMap.get(JSON.stringify(e)));

    for (let i = 0; i < clearedErrors.length; i++) {
        const error = clearedErrors[i];
        globalStore.dispatch({
            type: 'messages/removeMessage',
            payload: `projectError${error.featureId}${error.settingId}`,
        });
    }


    const newErrors = errorsList.filter(e => !previousErrorsMap.get(JSON.stringify(e)));

    for (let i = 0; i < newErrors.length; i++) {
        const error = newErrors[i];
        globalStore.dispatch({
            type: 'messages/addMessage',
            payload: {
                id: `projectError${error.featureId}${error.settingId}`,
                title: 'Project Error',
                message: error.message,
                action: {
                    type: 'pages/setShowErrors',
                    payload: true,
                    label: 'View',
                },
                delay: 5000,
            },
        });
    }

    globalStore.dispatch({
        type: 'project/setFeatureErrors',
        payload: errorsHashmap,
    });
    let changeCount = 0;
    for (let i = 0; i < validationMessages.length; i++) {
        const message = validationMessages[i];
        switch (message.messageType) {
            case 'add':
                globalStore.dispatch({
                    type: 'project/addFeature',
                    payload: {
                        feature: message.data,
                        featureId: message.featureId,
                        settingId: message.settingId,
                    },
                });
                changeCount++;
                break;
            case 'remove':
                globalStore.dispatch({
                    type: 'project/removeFeature',
                    payload: {
                        featureId: message.featureId,
                    },
                });
                changeCount++;
                break;
            case 'update':
                globalStore.dispatch({
                    type: 'project/updateFeatureSetting',
                    payload: {
                        featureId: message.featureId,
                        settingId: message.settingId,
                        value: message.data,
                    },
                });
                changeCount++;
                break;
            default:

                break;
        }
    }
    if (changeCount === 0) {
        globalStore.dispatch({
            type: 'project/setLastValidated',
            payload: new Date().valueOf(),
        });
    } else {
        globalStore.dispatch({
            type: 'project/setLastChanged',
            payload: new Date().valueOf(),
        });
    }
    globalStore.dispatch({
        type: 'messages/setLoadingMessage',
        payload: 'Saving Project...',
    });

    try {
        const user = globalStore.getState().user.user;
        if (user) {
            await axiosInstance.put(`${process.env.API_URL}/api/projects/${project.id}`, project);
        }
        if (!user && !inIframe()) {
            localStorage.setItem('project_newtemp', JSON.stringify(project));
        }
    } catch (ex) {
        console.log(`error saving project ${ex}`);
        // if (ex.response !== undefined && ex.response.status === 401) {
        //     const tmpProject = ProjectConfiguration.fromJson(project);
        //     tmpProject.id = 'newtemp';
        //     this.setState({
        //         project: tmpProject,
        //         loading: false,
        //     });
        // }
        // this.addMessage('Error Saving Project', 'Project Save Error', 3000);
    } finally {
        globalStore.dispatch({
            type: 'messages/setLoadingMessage',
            payload: '',
        });
    }
};

projectMiddleware.startListening({
    actionCreator: setProject,
    effect: async (action, store) => {
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: setProjectProperty,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        project[action.payload.property] = action.payload.value;
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: updateFeatureSetting,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        const {
            featureId, settingId, value,
        } = action.payload;
        const updatedSettings = featureId ? {} : action.payload.updatedSettings;
        if (featureId) {
            updatedSettings[featureId] = { ...action.payload.updatedSettings };
            if (settingId) {
                updatedSettings[featureId][settingId] = value;
            }
        }
        const featureIds = Object.keys(updatedSettings);
        for (let i = 0; i < featureIds.length; i++) {
            const featureId = featureIds[i];
            const settingIds = Object.keys(updatedSettings[featureId]);
            for (let j = 0; j < settingIds.length; j++) {
                const settingId = settingIds[j];
                const settingValue = updatedSettings[featureId][settingId];
                FeatureUtils.updateFeatureSetting(
                    project, featureId, settingId, settingValue,
                );
            }
        }
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: updateFeatureSetting,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        const {
            featureId, settingId, value,
        } = action.payload;
        const feature = project.getFeature(featureId);
        if (feature && feature.id === 'cgg' && settingId === 'ENABLED' && value === '1') {
            const deviceID = feature.getSettingValue('USERNAME');
            const password = feature.getSettingValue('PASSWORD');
            if (deviceID === '""' && password === '""') {
                globalStore.dispatch({
                    type: 'messages/addMessage',
                    payload: {
                        id: 'prompt_cloud_device_creation',
                        title: 'Cloud Device',
                        message: `A Device is required to enable AppBlocks Cloud . Would you like to create one now? \n
                        (You can also do this manually later)`,
                        action: {
                            type: 'project/createCloudDevice',
                            payload: {
                                deviceName: project.name,
                            },
                            label: 'Create',
                        },
                    },
                });
            }
        }
    },
});

projectMiddleware.startListening({
    actionCreator: createCloudDevice,
    effect: async (action, store) => {
        const workspace = store.getState().workspaces.currentWorkspace;
        try {
            const newCloudDevice = await axiosInstance.post(`${process.env.API_URL}/cgg/devices`, {
                name: action.payload.deviceName,
                workspace,
            });
            if (newCloudDevice.status === 200) {
                store.dispatch({
                    type: 'messages/removeMessage',
                    payload: 'prompt_cloud_device_creation',
                });
                store.dispatch({
                    type: 'project/updateFeatureSetting',
                    payload: {
                        featureId: 'cgg',
                        settingId: 'USERNAME',
                        value: newCloudDevice.data.deviceId,
                    },
                });
                store.dispatch({
                    type: 'project/updateFeatureSetting',
                    payload: {
                        featureId: 'cgg',
                        settingId: 'PASSWORD',
                        value: newCloudDevice.data.key,
                    },
                });
                store.dispatch({
                    type: 'messages/addMessage',
                    payload: {
                        id: 'cloud_device_creation_success',
                        title: 'Cloud Device',
                        message: 'Cloud device created successfully',
                        cancellable: false,
                        delay: 5000,
                    },
                });
            } else {
                throw new Error('Error creating cloud device');
            }
        } catch (e) {
            store.dispatch({
                type: 'messages/addMessage',
                payload: {
                    id: 'cloud_device_creation_error',
                    title: 'Cloud Device',
                    message: 'Error creating cloud device',
                    cancellable: false,
                },
            });
        }
    },
});

projectMiddleware.startListening({
    actionCreator: addFeature,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        const { feature, settingId, featureId } = action.payload;
        FeatureUtils.addFeature(project, feature, settingId, featureId);
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: addFeatures,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        const { features } = action.payload;
        for (let i = 0; i < features.length; i++) {
            FeatureUtils.addFeature(
                project,
                features[i].feature,
                features[i].settingId,
                features[i].featureId,
            );
        }
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: removeFeature,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        const { featureId } = action.payload;
        FeatureUtils.removeFeature(project, featureId);
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: removeFeatures,
    effect: async (action, store) => {
        const project = ProjectInstance.getInstance();
        const { features } = action.payload;
        for (let i = 0; i < features.length; i++) {
            FeatureUtils.removeFeature(
                project,
                features[i].featureId,
            );
        }
        pushUpdate(store);
    },
});

projectMiddleware.startListening({
    actionCreator: swapFeatures,
    effect: async (action, store) => {
        const {
            featureId, settingId, featureId1, featureId2,
        } = action.payload;
        const project = ProjectInstance.getInstance();
        const feature = project.getFeature(featureId);
        const arr = feature.getSettingValue(settingId);
        const index1 = arr.findIndex(f => f.getID() === featureId1);
        const index2 = arr.findIndex(f => f.getID() === featureId2);
        const tmp = arr[index1];
        arr[index1] = arr[index2];
        arr[index2] = tmp;
        pushUpdate(store);
    },
});


projectMiddleware.startListening({
    actionCreator: getProjectCode,
    effect: async (action, store) => {
        const state = store.getState();
        if (state.projectData.code.length > 0) {
            return;
        }
        if (state.projectData.loadingCode) {
            return;
        }
        const project = ProjectInstance.getInstance().toJson();
        if (Object.keys(project).length === 0) {
            return;
        }
        store.dispatch({
            type: 'project/setProjectCodeLoading',
            payload: true,
        });
        axiosInstance.post(`${process.env.API_URL}/api/projects/code/`, {
            params: {
                project,
            },
        })
            .then((response) => {
                const newState = store.getState();
                const responseFiles = response.data;
                store.dispatch({
                    type: 'project/setProjectCodeLoading',
                    payload: false,
                });
                if (newState.projectData.projectId !== state.projectData.projectId) {
                    return;
                }
                store.dispatch({
                    type: 'project/setProjectCode',
                    payload: responseFiles,
                });
                if (newState.debug.consoleHistory === 'Code could not be generated.\n') {
                    store.dispatch({
                        type: 'debug/clearConsole',
                        payload: '',
                    });
                }
            })
            .catch((e) => {
                store.dispatch({
                    type: 'project/setProjectCodeLoading',
                    payload: false,
                });
                store.dispatch({
                    type: 'messages/removeMessage',
                    payload: 'codeGenerationError',
                });
                store.dispatch({
                    type: 'messages/addMessage',
                    payload: {
                        id: 'codeGenerationError',
                        title: 'Code Generation Error',
                        message: 'Code could not be generated',
                        cancellable: false,
                    },
                });
            });
    },
});

projectMiddleware.startListening({
    actionCreator: projectSetDebugTarget,
    effect: async (action, store) => {
        const debug = store.getState().debug;
        if (debug.projectId === action.payload.projectId) {
            store.dispatch({
                type: 'debug/setDevice',
                payload: action.payload.device,
            });
        }
    },
});

projectMiddleware.startListening({
    actionCreator: projectSetUploadMethod,
    effect: async (action, store) => {
        const debug = store.getState().debug;
        if (debug.projectId === action.payload.projectId) {
            store.dispatch({
                type: 'debug/setUploadMethod',
                payload: action.payload.uploadMethod,
            });
        }
    },
});


const updateTimer = setInterval(doUpdate, SAVE_INTERVAL);

export default projectMiddleware;
